import { useContext, useState } from 'react';

import getGridTotalsRow from 'api/getGridTotalsRow';
import FilterPlatesContext from 'contexts/FilterPlatesContext';
import GqlClientContext from 'contexts/GqlClientContext';
import GridContext from 'contexts/GridContext';
import useDateScope from 'hooks/useDateScope';
import useGetDatasetDefinition from 'hooks/useGetDatasetDefinition';
import useNetworkingEffect from 'hooks/useNetworkingEffect';

import getValidAggregateFields from './getValidAggregateFields';
import { TOTALS_QUERY } from './gridConstants';

// The grid's totals row (the aggregate fields over the same filters), as AG
// pinned-bottom row data
const useGridTotalsRow = ({
  baseView,
  filterInput,
  gridTotalsQueryOverride,
  hideTotalsRow,
}: {
  baseView: FleetOps.BaseView;
  filterInput: FilterInput[];
  gridTotalsQueryOverride?: string;
  hideTotalsRow?: boolean;
}) => {
  const { quickFilters } = useContext(FilterPlatesContext);
  const { client } = useContext(GqlClientContext);
  const { dataType } = useContext(GridContext);
  const getDatasetDefinition = useGetDatasetDefinition();
  const dateScope = useDateScope({});
  const [totalsRow, setTotalsRow] = useState<any>();

  useNetworkingEffect(() => {
    if (hideTotalsRow) {
      return;
    }
    const fields = getValidAggregateFields({
      baseView,
      getDatasetDefinition,
    });
    let isActive = true;

    if (fields.length === 0 && baseView.type !== 'commission') {
      setTotalsRow([]);
      return;
    }

    getGridTotalsRow({
      query: gridTotalsQueryOverride ? gridTotalsQueryOverride : TOTALS_QUERY,
      dateScope,
      dataType,
      filters: filterInput,
      fields,
      client,
    }).then((totals: any) => {
      if (!isActive) {
        return;
      }
      const row = totals[0];
      if (!row) {
        setTotalsRow(undefined);
        return;
      }

      const fields = Object.keys(row) as string[];
      fields.forEach((f) => {
        const parts = f.split('.');
        if (parts.length === 2) {
          if (!row[parts[0]]) {
            row[parts[0]] = {};
          }
          row[parts[0]][parts[1]] = row[f];
        }
      });

      setTotalsRow([row]);
    });
    return () => {
      isActive = false;
    };
  }, [
    baseView,
    client,
    dataType,
    dateScope,
    filterInput,
    getDatasetDefinition,
    gridTotalsQueryOverride,
    hideTotalsRow,
    quickFilters,
  ]);

  return totalsRow;
};

export default useGridTotalsRow;
